import React, { useContext, useEffect, useState } from "react";
import { Breadcrumb} from "react-bootstrap";
import { Button} from '@blueprint/ui';
import { NavLink, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";

import invoiceApi from "../../api/invoice";
import { Loader, toast } from "../../common/components/core";
import { FormFooter } from "../../common/components/form";
import { useAuthErrorHandlerCreator } from "../../common/hooks";
import { currentUserSelector } from "../../redux/slices/auth/selectors";
import { BesuClaimState } from "../../types/claim";
import { BankAccount } from "../../types/common";
import { BesuProjectState } from "../../types/project";
import { PageTitle } from "../../types/pageTitle";
import { shortenUUID } from "../../utils/common";
import { composeErrorHandlers, GenericError } from "../../utils/errorHandling";
import ClaimContext from "../claim/ClaimContext";
import ProjectContext from "../project/ProjectContext";

import InvoiceForm, { InvoiceFormValues } from "./components/InvoiceForm";
import InvoicePreview from "./components/InvoicePreview";
import { useDeveloperBankAccountList } from "../../api/utilities";

const NewInvoiceView = (): JSX.Element => {
    document.title = PageTitle.NewInvoiceView;
    const {
        data: project,
    } = useContext(ProjectContext);
    const {
        data: claim
    } = useContext(ClaimContext);
    const currentUser = useSelector(currentUserSelector);
    const { data: bankAccounts, isValidating: isBankAccountsLoading } =
        useDeveloperBankAccountList(currentUser?.userProfile.id);
    const history = useHistory();
    const onAuthError = useAuthErrorHandlerCreator();
    const [isSubmitting, setSubmitting] = useState(false);

    const submit = async (values: InvoiceFormValues) => {
        setSubmitting(true);
        try {
            const res = await invoiceApi.create(
                claim.referenceId,
                values,
                bankAccounts ?? [],
            );
            const newInvoiceId =
                res.stateAndRefMap.InvoiceState?.[0]?.state.data.linearId.id;
            toast(`Invoice #${shortenUUID(newInvoiceId ?? "")} submitted`);
            history.push(
                `/project/${project.referenceId}/claim/${claim.referenceId}/invoice/${newInvoiceId}`,
            );
        } catch (e) {
            setSubmitting(false);
            composeErrorHandlers(onAuthError)(e as GenericError);
        }
    };

    return (
        <PureNewInvoiceView
            project={project}
            claim={claim}
            bankAccountList={bankAccounts ?? []}
            isLoading={isSubmitting || isBankAccountsLoading}
            onSubmit={submit}
            onCancel={() =>
                history.push(
                    `/project/${project.referenceId}/claim/${claim.referenceId}`,
                )
            }
        />
    );
};

type PureNewInvoiceViewProps = {
    project: BesuProjectState;
    claim: BesuClaimState;
    bankAccountList: BankAccount[];
    isLoading: boolean;
    onSubmit: (values: InvoiceFormValues) => void;
    onCancel: () => void;
};

export const PureNewInvoiceView = ({
    project,
    claim,
    bankAccountList,
    isLoading,
    onSubmit,
    onCancel,
}: PureNewInvoiceViewProps): JSX.Element => {
    const [isPreview, setIsPreview] = useState(false);
    const [formValues, setFormValues] = useState<InvoiceFormValues>();

    useEffect(() => {
        if (!isLoading) {
            setIsPreview(false);
        }
    }, [isLoading]);

    const preview = (values: InvoiceFormValues) => {
        setFormValues(values);
        setIsPreview(true);
    };

    return (
        <Loader overlay isLoading={isLoading} bgClassName="bg-body">
            <Breadcrumb className="mb-5">
                <Breadcrumb.Item linkAs="span">
                    <NavLink to="/project">Project List</NavLink>
                </Breadcrumb.Item>
                <Breadcrumb.Item linkAs="span">
                    <NavLink to={`/project/${project.referenceId}`}>
                        {project.name}
                    </NavLink>
                </Breadcrumb.Item>
                <Breadcrumb.Item linkAs="span">
                    <NavLink
                        to={`/project/${project.referenceId}/claim/${claim.referenceId}`}
                    >
                        Claim {shortenUUID(claim.referenceId)}
                    </NavLink>
                </Breadcrumb.Item>
                <Breadcrumb.Item active>New Invoice</Breadcrumb.Item>
            </Breadcrumb>
            {formValues && isPreview ? (
                <>
                    <h1 className="mb-3">Preview</h1>
                    <InvoicePreview
                        bankAccountList={bankAccountList}
                        formValues={formValues}
                    />
                    <hr className="bg-border-grey my-2" />
                    <FormFooter className="d-flex justify-content-between">
                        <Button
                            variant="light.tertiary"
                            onClick={() => setIsPreview(false)}
                        >
                            Back
                        </Button>
                        <Button
                            variant="light.primary"
                            onClick={() => onSubmit(formValues)}
                        >
                            Submit
                        </Button>
                    </FormFooter>
                </>
            ) : (
                <InvoiceForm
                    bankAccountList={bankAccountList}
                    values={formValues}
                    onCancel={onCancel}
                    onSubmit={preview}
                />
            )}
        </Loader>
    );
};

export default NewInvoiceView;
